/**
 * ClientTimeline
 * Complete timeline view for a client with filters and pagination
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Filter, Loader2, AlertCircle, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { format, startOfDay, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { timelineService } from '@/services/timeline-service';
import { TimelineEvent } from './TimelineEvent';
import toast from '@/lib/toast';
import type {
  TimelineEvent as TimelineEventType,
  TimelineEventType as EventType
} from '@/types/timeline';

import { getErrorMessage } from '../../utils/error.utils'
import { parseDbTimestamp } from '@/utils/datetime';

const EVENT_TYPE_FILTERS: { type: EventType; label: string }[] = [
  { type: 'interaction_created', label: '📋 Atendimentos' },
  { type: 'case_created', label: '📂 Casos' },
  { type: 'legal_process_created', label: '⚖️ Processos Judiciais' },
  { type: 'interaction_converted_to_case', label: '🔄 Conversões' },
  { type: 'task_created', label: '🔔 Tarefas' },
  { type: 'document_uploaded', label: '📄 Documentos' }
];

export interface ClientTimelineProps {
  clientId: string;
  showFilters?: boolean;
  limit?: number;
  onEventClick?: (event: TimelineEventType) => void;
}

export function ClientTimeline({
  clientId,
  showFilters = true,
  limit = 20,
  onEventClick
}: ClientTimelineProps) {

  const [events, setEvents] = useState<TimelineEventType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  // Filters
  const [selectedTypes, setSelectedTypes] = useState<EventType[]>([]);
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const requestSeqRef = useRef(0);

  // Load timeline
  const loadTimeline = useCallback(async (page: number = 1) => {
    const requestSeq = requestSeqRef.current + 1;
    requestSeqRef.current = requestSeq;
    setLoading(true);
    setError(null);

    try {
      const response = await timelineService.getTimelineWithPagination(
        clientId,
        page,
        limit,
        {
          types: selectedTypes.length > 0 ? selectedTypes : undefined,
          fromDate: fromDate || undefined,
          toDate: toDate || undefined
        }
      );

      if (requestSeq !== requestSeqRef.current) {
        return;
      }

      if (page === 1) {
        setEvents(response.events);
      } else {
        setEvents(prev => {
          const seen = new Set(prev.map(event => event.id));
          return [
            ...prev,
            ...response.events.filter(event => !seen.has(event.id))
          ];
        });
      }
      setCurrentPage(response.pagination.page);
      setTotalPages(response.pagination.totalPages);
      setHasMore(response.pagination.hasNext);

    } catch (err: unknown) {
      if (requestSeq !== requestSeqRef.current) {
        return;
      }
      setError(getErrorMessage(err, 'Erro ao carregar timeline'));
      toast.error('Erro ao carregar timeline');
    } finally {
      if (requestSeq === requestSeqRef.current) {
        setLoading(false);
      }
    }
  }, [clientId, fromDate, limit, selectedTypes, toDate]);

  // Initial load
  useEffect(() => {
    loadTimeline(1);
  }, [loadTimeline]);

  // Toggle event type filter
  const toggleEventType = (type: EventType) => {
    setSelectedTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  // Clear filters
  const clearFilters = () => {
    setSelectedTypes([]);
    setFromDate('');
    setToDate('');
  };

  // Load more (pagination)
  const loadMore = () => {
    if (hasMore && !loading) {
      loadTimeline(currentPage + 1);
    }
  };

  // Group events by date
  const groupedEvents = events.reduce((acc, event) => {
    const eventDate = parseDbTimestamp(event.createdAt);
    if (!eventDate) return acc;
    const date = format(eventDate, 'yyyy-MM-dd');
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(event);
    return acc;
  }, {} as Record<string, TimelineEventType[]>);

  const sortedDates = Object.keys(groupedEvents).sort((a, b) => b.localeCompare(a));

  // Format date header
  const formatDateHeader = (dateStr: string): string => {
    const date = parseISO(dateStr);
    const today = startOfDay(new Date());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (format(date, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd')) {
      return 'Hoje';
    }

    if (format(date, 'yyyy-MM-dd') === format(yesterday, 'yyyy-MM-dd')) {
      return 'Ontem';
    }

    return format(date, "d 'de' MMMM 'de' yyyy", { locale: ptBR });
  };

  const hasFilters = selectedTypes.length > 0 || Boolean(fromDate) || Boolean(toDate);

  return (
    <div className="flex flex-col gap-4">
      {showFilters && (
        <Card size="sm">
          <CardContent className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <Filter className="size-4 text-muted-foreground" aria-hidden="true" />
              <span className="text-sm font-medium">Filtros</span>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-xs font-medium text-muted-foreground">Tipos de evento</span>
              <div className="flex flex-wrap gap-2">
                {EVENT_TYPE_FILTERS.map(({ type, label }) => {
                  const active = selectedTypes.includes(type);
                  return (
                    <Button
                      key={type}
                      type="button"
                      size="sm"
                      variant={active ? 'secondary' : 'outline'}
                      aria-pressed={active}
                      className="rounded-full"
                      onClick={() => toggleEventType(type)}
                    >
                      {label}
                    </Button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`timeline-from-${clientId}`} className="text-xs">
                  <Calendar className="size-3" aria-hidden="true" /> Data inicial
                </Label>
                <Input
                  id={`timeline-from-${clientId}`}
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`timeline-to-${clientId}`} className="text-xs">
                  <Calendar className="size-3" aria-hidden="true" /> Data final
                </Label>
                <Input
                  id={`timeline-to-${clientId}`}
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
            </div>

            {hasFilters && (
              <Button type="button" variant="ghost" size="sm" className="self-start" onClick={clearFilters}>
                Limpar filtros
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {loading && events.length === 0 && (
        <div className="flex flex-col gap-3" aria-busy="true">
          <span className="sr-only">Carregando timeline...</span>
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-20" />)}
        </div>
      )}

      {error && (
        <div role="alert" className="flex flex-col items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-center">
          <AlertCircle className="size-6 text-destructive" aria-hidden="true" />
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" size="sm" onClick={() => loadTimeline(1)}>Tentar novamente</Button>
        </div>
      )}

      {!loading && !error && events.length === 0 && (
        <div className="flex flex-col items-center gap-2 rounded-lg border bg-muted/40 py-12 text-center">
          <p className="text-sm text-muted-foreground">Nenhum evento encontrado</p>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>Limpar filtros</Button>
          )}
        </div>
      )}

      {!loading && !error && events.length > 0 && (
        <div className="flex flex-col gap-6">
          {sortedDates.map((date) => (
            <div key={date}>
              <div className="mb-3 flex items-center gap-3">
                <div className="h-px flex-1 bg-border" />
                <h3 className="text-sm font-semibold text-muted-foreground">{formatDateHeader(date)}</h3>
                <div className="h-px flex-1 bg-border" />
              </div>
              <div className="flex flex-col gap-3">
                {groupedEvents[date].map((event) => (
                  <TimelineEvent key={event.id} event={event} onClick={onEventClick} />
                ))}
              </div>
            </div>
          ))}

          {hasMore && (
            <div className="flex flex-col items-center gap-2 pt-4">
              <Button variant="outline" onClick={loadMore} disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Carregando...
                  </>
                ) : (
                  'Carregar mais'
                )}
              </Button>
              <p className="text-xs text-muted-foreground">Página {currentPage} de {totalPages}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
